import { useEffect, useMemo, useRef } from 'react'
import { Avatar, Box, Flex, Skeleton, useMantineTheme } from '@mantine/core'
import dayjs from 'dayjs'

import type { ChatMessagesProps } from '@/components/@Shared/Chat/partials/ChatMessages/ChatMessages.props'
import { ScrollArea } from '@/components/ScrollArea/ScrollArea'
import { Text } from '@/components/Text/Text'

import type { MessageData } from '@/__generated__/requests'

import classes from './ChatMessages.module.css'

export function ChatMessages({
  fetchNextPage,
  isFetchingNextPage,
  isLoading,
  messages,
  recipientImage,
  senderId,
}: ChatMessagesProps) {
  const theme = useMantineTheme()

  const viewportRef = useRef<HTMLDivElement>(null)
  const previousScrollHeight = useRef(0)
  const previousScrollTop = useRef(0)
  const previousIsFetchingNextPage = useRef(false)

  const handleFetchNextPage = async () => {
    if (viewportRef.current) {
      previousScrollHeight.current = viewportRef.current.scrollHeight
      previousScrollTop.current = viewportRef.current.scrollTop
    }

    fetchNextPage()
  }

  const groupMessages = useMemo<MessageData[][]>(() => {
    if (!messages.length) return []

    const groupedMessages = []
    let currentGroup = [messages[0]]

    messages.slice(1).forEach((currentMessage, index) => {
      const previousMessage = messages[index]

      const diffInHours = dayjs(currentMessage.time).diff(
        dayjs(previousMessage.time),
        'hour',
      )

      if (diffInHours < 3) {
        currentGroup.push(currentMessage)
      } else {
        groupedMessages.push(currentGroup)
        currentGroup = [currentMessage]
      }
    })

    groupedMessages.push(currentGroup)

    return groupedMessages
  }, [messages])

  useEffect(() => {
    if (viewportRef.current) {
      if (previousIsFetchingNextPage.current && !isFetchingNextPage) {
        const newScrollHeight = viewportRef.current.scrollHeight
        const heightDiff = newScrollHeight - previousScrollHeight.current

        viewportRef.current.scrollTop = previousScrollTop.current + heightDiff

        previousScrollHeight.current = 0
        previousScrollTop.current = 0
      } else if (!previousIsFetchingNextPage.current && !isFetchingNextPage) {
        viewportRef.current.scrollTop = viewportRef.current.scrollHeight
      }
    }

    previousIsFetchingNextPage.current = isFetchingNextPage
  }, [messages, isFetchingNextPage])

  return (
    <ScrollArea
      className={classes.viewport}
      onTopReached={handleFetchNextPage}
      scrollbars="y"
      viewportRef={viewportRef}
    >
      {isLoading && (
        <Flex>
          <Skeleton className={classes.skeleton} />
        </Flex>
      )}

      {groupMessages.map(messages => (
        <Box key={messages[0].time}>
          <Flex justify="center" mb={16}>
            <Text variant="body_2">
              {dayjs(messages[0].time).format('MMMM D, h:mm A')}
            </Text>
          </Flex>

          {messages.map((message, index) => {
            const isMe = senderId === message.userId
            const prevMessage = messages[index - 1]
            const isPrevMessageSameUser = prevMessage?.userId === message.userId

            const shouldShowAvatar =
              !isMe && (!prevMessage || prevMessage.userId !== message.userId)

            return (
              <Flex key={message.id}>
                {shouldShowAvatar && (
                  <Avatar
                    c={theme.other.colors.grey}
                    h={40}
                    mr={8}
                    src={recipientImage}
                    w={40}
                  />
                )}
                <Box
                  className={`${classes.bubble} ${
                    isMe ? classes.me : classes.recipient
                  } ${isPrevMessageSameUser ? classes.afterSameUser : ''}`}
                >
                  <Text variant="body_2_black">{message.content}</Text>
                </Box>
              </Flex>
            )
          })}
        </Box>
      ))}
    </ScrollArea>
  )
}
